import { useCallback, useEffect, useState } from "react";
import { ScrollText } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { OpsShell } from "@/components/admin/OpsShell";
import { Card, Empty, fmtDate, Loading, Pager, T } from "@/components/admin/ui";

const short = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  return typeof v === "object" ? JSON.stringify(v) : String(v);
};

const ROLE_TONE = {
  super_admin: "text-emerald-700 dark:text-emerald-400",
  admin: "text-blue-700 dark:text-blue-400",
  support: "text-slate-500 dark:text-slate-400",
};

export default function AdminAudit() {
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/audit?page=${page}&page_size=50`);
      setD(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <OpsShell title="Audit log" subtitle="Immutable record of every state-changing admin action">
      <Card testid="admin-audit-panel" title="Admin actions" hint={d ? `${d.total} entries` : ""}>
        {!d ? (
          <Loading label="Loading audit trail" />
        ) : d.items.length === 0 ? (
          <Empty label="No admin actions recorded yet" hint="Every change you make here will be logged" icon={ScrollText} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[1040px]">
                <thead>
                  <tr>
                    <th className={T.th}>When</th>
                    <th className={T.th}>Admin</th>
                    <th className={T.th}>Action</th>
                    <th className={T.th}>Target</th>
                    <th className={T.th}>Before</th>
                    <th className={T.th}>After</th>
                    <th className={T.th}>Reason</th>
                    <th className={T.th}>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {d.items.map((a) => (
                    <tr key={a.id} className={T.row} data-testid={`admin-audit-row-${a.id}`}>
                      <td className={`${T.td} ${T.num} ${T.muted} whitespace-nowrap`}>{fmtDate(a.created_at)}</td>
                      <td className={T.td}>
                        <p className="text-[13px] truncate max-w-[190px]">{a.admin_email}</p>
                        <p className={`text-[10px] uppercase tracking-wide font-medium ${ROLE_TONE[a.admin_role] || ROLE_TONE.support}`}>
                          {a.admin_role?.replace("_", " ")}
                        </p>
                      </td>
                      <td className={`${T.td} text-[13px] font-medium text-emerald-700 dark:text-emerald-400`}>{a.action}</td>
                      <td className={`${T.td} text-xs ${T.muted} truncate max-w-[180px]`}>{a.target_email}</td>
                      <td className={`${T.td} ${T.num} text-xs ${T.muted} max-w-[150px] truncate`}>{short(a.before)}</td>
                      <td className={`${T.td} ${T.num} text-xs max-w-[150px] truncate`}>{short(a.after)}</td>
                      <td className={`${T.td} text-xs italic ${T.muted} max-w-[220px] truncate`}>{a.reason || "—"}</td>
                      <td className={`${T.td} ${T.num} text-[10px] ${T.muted}`}>{a.ip_hash || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={d.page} pages={d.pages} total={d.total} onPage={setPage} testid="admin-audit-pager" />
          </>
        )}
      </Card>
    </OpsShell>
  );
}
